/**
 * Which recording a reviewer HEARS — the rule, pulled out of the player so it
 * can be tested without a browser.
 *
 * Exactly one track is ever audible: the cameras and the screen capture the
 * same room, so unmuting two plays the same voice twice, a few hundred
 * milliseconds apart.
 */
import type { RecordingSource } from "@exam/shared";
import type { Track } from "../data/types";

/** Best guess first: the laptop webcam carries the student's microphone. */
const PREFERENCE = ["webcam", "phone", "screen"] as const;

/**
 * Compile error the day `RecordingSource` gains a member: a source missing
 * from `PREFERENCE` would silently fall through to "whatever is first".
 */
type _EverySourceRanked =
  Exclude<RecordingSource, (typeof PREFERENCE)[number]> extends never
    ? true
    : never;

/**
 * The tracks worth offering as the sound: footage exists AND it can be
 * decoded.
 *
 * `header_missing` is the trap. Such a track has chunks — it looks like a
 * recording in every listing — but chunk 0 never arrived, so nothing in it
 * plays, audio included. Left in, an unplayable webcam would win the default
 * over a phone that recorded the room perfectly, and the reviewer would hear
 * silence with the volume at full: exactly the "the mic never worked"
 * conclusion this control exists to prevent.
 */
export function audioCandidates(tracks: Track[]): RecordingSource[] {
  return tracks
    .filter((t) => t.chunks.length > 0 && !t.header_missing)
    .map((t) => t.source);
}

/**
 * The track whose sound plays, or null when this attempt has no footage.
 *
 * `chosen` wins when it is one of this attempt's tracks. It is checked rather
 * than trusted because a reviewer moving from one attempt to the next carries
 * their choice with them, and a phone the next student never paired would
 * otherwise silence a recording that does have sound.
 */
export function audibleSource(
  candidates: readonly RecordingSource[],
  chosen: RecordingSource | null,
): RecordingSource | null {
  if (chosen !== null && candidates.includes(chosen)) return chosen;
  for (const source of PREFERENCE) {
    if (candidates.includes(source)) return source;
  }
  return candidates[0] ?? null;
}

/** Whether a given track's `<video>` should be silent. */
export function isMutedFor(
  source: RecordingSource,
  audible: RecordingSource | null,
  muted: boolean,
): boolean {
  return muted || source !== audible;
}
